/**
 * @file 编辑公告任务全屏弹窗
 */
import { Button, State, Toast } from '@seiue/ui'
import { pick } from '@seiue/util'
import { Form } from 'antd'
import moment from 'moment'
import { FC, useEffect, useRef, useState } from 'react'

import {
  TaskStatusEnum,
  TaskTypeEnum,
  activityApi,
  announcementApi,
} from 'packages/sdks-next/growp'

import { CommonBasicInfoRef, SubmitValues } from '@/components/CommonBasicInfo'
import { ContentCard } from '@/components/ContentCard'
import { ModalLayout } from '@/components/ModalLayout'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'

import { EditTaskModalProps } from '../types'

import { BasicInfo } from './BasicInfo'

/**
 * 编辑公告任务弹窗
 *
 * @param param0 - 参数
 * @param param0.onClose - 关闭弹窗
 * @param param0.taskId - 任务 ID
 * @param param0.activityId - 活动 ID
 * @param param0.activityTaskOptions - 活动任务选项
 * @returns 组件
 */
export const EditAnnouncementTaskModal: FC<EditTaskModalProps> = ({
  onClose,
  activityId,
  taskId,
  activityTaskOptions,
}) => {
  const isEdit = !!taskId

  const [form] = Form.useForm()
  const [title, setTitle] = useState<string>('')
  const [commonBasicInfo, setCommonBasicInfo] = useState<SubmitValues>({})
  const basicInfoRef = useRef<CommonBasicInfoRef>(null)

  const { data: editTask, loading } =
    announcementApi.useLoadAnnouncementActivityTask(
      {
        id: taskId || 0,
        query: {
          isForeground: false,
        },
      },
      { disable: !taskId }
    )

  const filterTaskOptions =
    activityTaskOptions?.filter((o) => o.value !== taskId) || []

  const { api: createActivityTask } =
    announcementApi.useCreateAnnouncementActivityTask({
      reload: [activityApi.queryTasks, activityApi.loadActivity],
    })

  const { api: updateActivityTask } =
    announcementApi.useUpdateAnnouncementActivityTask({
      reload: [activityApi.queryTasks, activityApi.loadActivity],
    })

  useEffect(() => {
    form.resetFields()

    form.setFieldsValue({ ...editTask })
    setTitle(editTask?.name || '')

    setCommonBasicInfo({
      ...pick({ ...editTask }, [
        'endAt',
        'adminIds',
        'timedStartAt',
        'prevTaskIds',
      ]),
      startAt: editTask
        ? editTask.startAt
        : moment().format('YYYY-MM-DD HH:mm:ss'),
      isShow: editTask?.isShow !== undefined ? editTask.isShow : true,
    })
  }, [editTask, form])

  if (loading) return <State.Loading />
  return (
    <ModalLayout
      title={
        <TaskHeaderLeft
          title={title || '未命名公告'}
          onClose={onClose}
        />
      }
      headerRight={
        <>
          <Button
            type="primary"
            onClick={async () => {
              form.submit()
              const commonRefInfo = basicInfoRef.current?.getBasicInfo()

              form.validateFields().then(async (values) => {
                const data = {
                  ...pick(values, ['name', 'description']),
                  ...(commonRefInfo ?? {}),
                  activityId,
                  type: TaskTypeEnum.Announcement,
                  status: TaskStatusEnum.Published,
                }
                if (isEdit) {
                  await updateActivityTask({
                    id: taskId,
                    payload: data,
                  })
                } else {
                  await createActivityTask(data)
                }

                Toast.success(isEdit ? '更新任务成功' : '保存任务成功')

                onClose()
              })
            }}
          >
            {isEdit ? '更新' : '确认'}
          </Button>
        </>
      }
      content={
        <ContentCard
          style={{ minHeight: 200 }}
          contentStyle={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <BasicInfo
            setTitle={setTitle}
            form={form}
            commonBasicInfo={commonBasicInfo}
            basicInfoRef={basicInfoRef}
            activityTaskOptions={filterTaskOptions}
            openTypeDisabled={editTask?.isSubmit}
          />
        </ContentCard>
      }
    />
  )
}
